import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { CheckCircle2, Filter } from 'lucide-react';
import { portfolioProjects } from '../data/mock';

const categories = ["All", "Web Development", "Custom Software", "Mobile App", "Cloud Services"];

const Portfolio = () => {
  const [filter, setFilter] = useState("All");

  const filteredProjects = portfolioProjects.filter(project => {
    if (filter === "All") return true;
    if (filter === "Web Development") return project.category.includes("Web") || project.category.includes("E-commerce");
    if (filter === "Mobile App") return project.category.includes("Mobile") || project.category.includes("App");
    return project.category === filter;
  });

  return (
    <section id="portfolio" className="py-20 lg:py-32 bg-white min-h-screen">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8"
          >
             <span className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-teal-100 to-cyan-100 text-teal-700 rounded-full text-sm font-medium mb-4">
              Our Work
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Recent Projects
            </h2>
            <p className="text-lg text-gray-600">
              Explore our diverse portfolio of digital solutions
            </p>
          </motion.div>

          {/* Filter Buttons */}
          <motion.div 
            className="flex flex-wrap justify-center gap-3 mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                  filter === cat 
                    ? 'bg-gradient-to-r from-teal-500 to-cyan-600 text-white shadow-lg shadow-teal-200 scale-105' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </motion.div>
        </div>

        {/* Portfolio Grid */}
        <motion.div 
          layout
          className="grid md:grid-cols-2 lg:grid-cols-2 gap-8 max-w-6xl mx-auto"
        >
          <AnimatePresence mode='popLayout'>
            {filteredProjects.map((project) => (
              <motion.div
                layout
                key={project.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
              >
                <Card 
                  className="border-2 border-gray-100 hover:border-teal-100 hover:shadow-2xl transition-all duration-300 overflow-hidden group h-full bg-white"
                >
                  {/* Project Image */}
                  <div className="relative h-64 overflow-hidden">
                    <motion.img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover"
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-90"></div>
                    
                    <div className="absolute inset-0 p-6 flex flex-col justify-end">
                      <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                        <div className="flex justify-between items-end mb-3">
                          <Badge className="bg-white/20 backdrop-blur-md text-white border-none">
                            {project.category}
                          </Badge>
                          <span className="text-xs text-gray-300 font-medium bg-black/30 px-2 py-1 rounded backdrop-blur-sm">
                            {project.clientType}
                          </span>
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2">
                          {project.title}
                        </h3>
                        <p className="text-gray-300 text-sm line-clamp-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-100">
                          {project.description}
                        </p>
                      </div>
                    </div>
                  </div>

                  <CardContent className="p-6">
                    {/* Features */}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-6">
                      {project.features.slice(0, 4).map((feature, index) => (
                        <div key={index} className="flex items-center space-x-2 text-sm text-gray-600">
                          <CheckCircle2 size={14} className="text-teal-500 flex-shrink-0" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* Result & Tech */}
                    <div className="space-y-4 pt-4 border-t border-gray-100">
                      <div>
                        <span className="text-xs font-semibold text-teal-600 uppercase tracking-wider">Result</span>
                        <p className="text-sm text-gray-700 mt-1">{project.result}</p>
                      </div>
                      

                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {filteredProjects.length === 0 && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <div className="inline-flex p-4 bg-gray-50 rounded-full mb-4 text-gray-400">
              <Filter size={32} />
            </div>
            <p className="text-xl text-gray-500">No projects found in this category.</p>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default Portfolio;
